import { navigate } from "gatsby"
import _t from "@core/i18n"
import type { PriceType } from "@core/types"
import type { CloudBluePlan } from "@gatsby-plugin-definitions/fragments/CloudBluePlan"
import type { Formily as FormilyType } from "@gatsby-plugin-generic-page/fragments/formily"
import { getDomainsPoolId } from "@gatsby-plugin-generic-page/helpers/getDomainsPoolId"
import { getUpsellData } from "@gatsby-plugin-generic-page/helpers/getUpsellData"
import get from "lodash/get"
import type { ComparableProduct } from "~/fragments/comparableProduct"
import isEmpty from "~/lib/isEmpty"
import { getSelectedPeriod } from "~/lib/plan"
import url from "~/lib/url"
import type { RootAppDispatch } from "~/store"
import {
  addItemsToBasket,
  showPlanOverLimitModal,
} from "~/store/basket/actions"
import { BasketActionSource } from "~/store/basket/constants"
import type { MarketplaceState } from "~/store/marketplace/selectors"
import type { PeriodName } from "~/types/squidex"
import { getSelectedPlanSettings } from "~/utils"
import findProductOptions from "./findProductOptions"

type TariffCardButtonResult = {
  text: string
  onClick: () => void
}

/**
 * Returns the label and click handler for a tariff card button in the parameters table.
 * Action selection is a priority waterfall — the first matching rule wins.
 *
 * @param item - product from the comparison table
 * @param productId - ID of the currently selected product (active card)
 * @param selectedPeriod - billing period chosen by the user
 * @param dispatch - Redux dispatcher
 * @param plansOverLimit - list of plan IDs that exceed the basket limit
 * @param currentPriceType - price type (net/gross)
 * @param marketplaceConfiguration - marketplace configuration state from the store
 * @param onFormilyFormClick - callback that opens a Formily form modal
 *
 * @returns `{ text, onClick }` object, or `null` when no button should be rendered
 */
export default function getTariffCardButtonProps(
  item: ComparableProduct,
  productId: string | undefined,
  selectedPeriod: PeriodName | undefined,
  dispatch: RootAppDispatch,
  plansOverLimit: number[],
  currentPriceType: PriceType,
  marketplaceConfiguration: MarketplaceState["configuration"],
  onFormilyFormClick: (form: FormilyType) => void
): TariffCardButtonResult | null {
  const {
    flatData: { saleConfigurations, modalForm, buttonText },
  } = item

  const productSaleConfig = saleConfigurations?.[0]

  const isCurrentSaleConfig =
    !!productSaleConfig && productSaleConfig.id === productId

  // Product configuration state from the marketplace store
  const configState = productSaleConfig
    ? marketplaceConfiguration[productSaleConfig.id]
    : null

  // Plan settings resolved from selected options (for the active product)
  const planSettings = configState
    ? getSelectedPlanSettings(
        productSaleConfig?.flatData.optionsMapping || [],
        configState.options
      )
    : null

  // Matching plan options for non-active products
  const productOptionsNonActive = productSaleConfig
    ? findProductOptions(planSettings, productSaleConfig)
    : null

  // Plan data from resolved options; falls back to the sale config's default plan
  const productOptionsPlanData: CloudBluePlan["flatData"] | undefined = get(
    isCurrentSaleConfig ? planSettings : productOptionsNonActive,
    "cloudBluePlan[0].flatData"
  )

  const defaultPlanData: CloudBluePlan["flatData"] | undefined = get(
    productSaleConfig,
    "flatData.defaultCloudBluePlan[0].flatData"
  )

  const productData = productOptionsPlanData || defaultPlanData

  // Period from plan options; overridden by the user's explicit selection
  const productOptionsPeriod = (
    isCurrentSaleConfig ? planSettings : productOptionsNonActive
  )?.period?.period

  const currentPeriod = selectedPeriod || productOptionsPeriod

  // Bonus screen URL (used in case 6)
  const bonusUrl = url.generateBonusUrl({
    p: productData?.alias,
    ax: getUpsellData(productSaleConfig?.flatData.upsell),
    period: currentPeriod,
    domainSearch: productSaleConfig
      ? getDomainsPoolId(productSaleConfig)
      : undefined,
    priceType: currentPriceType,
  })

  const isPlanOverLimit = productData
    ? plansOverLimit.includes(productData.id as number)
    : false

  // --- Priority waterfall ---

  // 1. Modal form defined at the product level
  if (!isEmpty(modalForm)) {
    return {
      text: buttonText || _t("marketplace.chooseButton"),
      onClick: () => onFormilyFormClick(modalForm[0]),
    }
  }

  // 2. No sale configuration — no button
  if (!productSaleConfig) {
    return null
  }

  // 3. Formily form in customPriceBox — opens a modal instead of navigating away
  const formilyForm: FormilyType | undefined = get(
    productSaleConfig,
    "flatData.customPriceBox.formilyForm[0]"
  )

  if (!isEmpty(formilyForm)) {
    return {
      text: buttonText || _t("marketplace.contactButton"),
      onClick: () => onFormilyFormClick(formilyForm),
    }
  }

  // 4. No plan data resolved — no button
  if (isEmpty(productData)) {
    return null
  }

  // 5. skipBonusScreen — add to basket directly and navigate to checkout
  if (productSaleConfig.flatData.skipBonusScreen) {
    return {
      text: buttonText || _t("marketplace.chooseButton"),
      onClick: isPlanOverLimit
        ? () => dispatch(showPlanOverLimitModal())
        : () => {
            const periodDefinitions = getSelectedPeriod(
              productData,
              selectedPeriod
            )

            dispatch(
              addItemsToBasket(
                [
                  {
                    plan: productData.id,
                    planPeriod: periodDefinitions.id,
                    quantity: 1,
                  },
                ],
                `async-button-${productSaleConfig.id}`,
                BasketActionSource.EMPTY,
                () => navigate(url.getBasketUrl())
              )
            )
          },
    }
  }

  // 6. Default — navigate to the bonus/upsell screen
  return {
    text: buttonText || _t("marketplace.chooseButton"),
    onClick: isPlanOverLimit
      ? () => dispatch(showPlanOverLimitModal())
      : () => window.location.assign(bonusUrl),
  }
}
